"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelFieldAnchor = exports.ModelMutatorAnchor = void 0;
const core_api_1 = require("@ikegps/core-api");
const geodesic_1 = require("../../../geo/geodesic");
const math_1 = require("../../../math");
const unit_1 = require("../../../unit");
const collection_field_1 = require("../collection.field");
const location_field_1 = require("./location.field");
class ModelMutatorAnchor extends collection_field_1.ModelMutator {
    // Mutator to edit a vector anchor manually
    manualAnchor(newValue) {
        if (newValue == null || newValue.distance == null || newValue.bearing == null) {
            this.state.setFieldValue(this.field.id, null);
            return;
        }
        // Force the anchor bearing to be between 0 and two Pi
        newValue.bearing = unit_1.IkeAngle.normalize(newValue.bearing);
        // Sometimes pitch is not set, It is a required field
        if (newValue.pitch == null || isNaN(newValue.pitch)) {
            newValue.pitch = 0;
        }
        newValue.collectionTool = CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
        this.state.setFieldValue(this.field.id, {
            provider: CollectionModelFieldAnchor.PROVIDER_MANUAL,
            value: newValue
        });
    }
    // Mutator when adding a new p2p vector
    p2p(state, placement) {
        if (!Array.isArray(state)) {
            throw new Error(`Field ${this.field.id}: Invalid capture state.`);
        }
        if (state.length < 2) {
            throw new Error(`Field ${this.field.id}: Invalid capture count "${state.length}".`);
        }
        this.state.setFieldValue(this.field.id, {
            provider: CollectionModelFieldAnchor.PROVIDER_P2P,
            captures: state.slice(),
            value: {
                collectionTool: CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR,
                placement
            }
        });
    }
    // Method used by viewVectorManualEdit
    manual(newValue) {
        if (newValue == null || newValue.distance == null || newValue.bearing == null) {
            this.state.setFieldValue(this.field.id, null);
            return;
        }
        const anchorManualData = {
            collectionTool: CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR,
            placement: newValue.placement,
            distance: newValue.distance,
            bearing: newValue.bearing,
            pitch: newValue.pitch,
            relatedSpanIds: newValue.relatedSpanIds
        };
        this.manualAnchor(anchorManualData);
    }
    // Mutator when adding anchor with gps position
    gps(newValue) {
        // Validate GPS coordinates
        if (!location_field_1.CollectionModelFieldLocation.isValidLocation(newValue)) {
            this.clear();
            return;
        }
        const value = {
            latitude: newValue.latitude,
            longitude: newValue.longitude,
            altitude: newValue.altitude,
            collectionTool: newValue.collectionTool,
            placement: newValue.placement,
            relatedSpanIds: newValue.relatedSpanIds
        };
        if (newValue.accuracy != null) {
            value.accuracy = newValue.accuracy;
        }
        const stateValue = {
            provider: 'gps',
            value
        };
        this.state.setFieldValue(this.field.id, stateValue);
    }
    // Set anntenaHeight value
    setAntennaHeight(antennaHeight) {
        var stateValue = this.state.getFieldValue(this.field.id);
        stateValue.antennaHeight = antennaHeight;
    }
    copy(from) {
        if (from.isPointToPoint()) {
            this.p2p(from.stateValue.captures, from.value.placement);
            return true;
        }
        if (from.isManual()) {
            this.manualAnchor(from.value);
            return true;
        }
        return false;
    }
}
exports.ModelMutatorAnchor = ModelMutatorAnchor;
class CollectionModelFieldAnchor extends collection_field_1.CollectionModelField {
    static isAnchorField(f) {
        return f.schema.type === CollectionModelFieldAnchor.TYPE;
    }
    static isCollectionWithVectorTool(value) {
        return value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
    }
    static isCollectionWithLocationTool(value) {
        return value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION;
    }
    get Mutator() {
        if (this._Mutator == null) {
            this._Mutator = new ModelMutatorAnchor(this);
        }
        return this._Mutator;
    }
    get value() {
        const stateValue = this.stateValue;
        if (stateValue == null) {
            return null;
        }
        if (stateValue.value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION) {
            // If a placement is not provided, then we want to default to Other
            const locationState = stateValue.value;
            locationState.placement = locationState.placement || CollectionModelFieldAnchor.DEFAULT_PLACEMENT;
            return locationState;
        }
        if (stateValue.value.collectionTool === CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR) {
            if (this.isManual()) {
                return stateValue.value;
            }
            if (this.isPointToPoint()) {
                const vectorValue = math_1.MultiPoint.Points(this.getCaptures());
                // To be backwards compatible, vertical values should not have a sign on them
                vectorValue.vertical = Math.abs(vectorValue.vertical);
                vectorValue.collectionTool = CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
                vectorValue.placement = stateValue.value.placement;
                return vectorValue;
            }
        }
        throw new Error(`Field ${this.id}: Invalid Provider type "${stateValue['provider']}"`);
    }
    /**
     * Get the raw location data calculating alternative altitudes
     *
     * @remarks This cannot be null
     */
    get raw() {
        if (!this.isGPS()) {
            return {};
        }
        const stateValue = this.stateValue;
        const isAntennaHeightMissing = stateValue.antennaHeight == null || stateValue.antennaHeight === 0;
        if (isAntennaHeightMissing) {
            return Object.assign(Object.assign({}, (0, location_field_1.extractLocationRaw)(stateValue)), { deviceAltitude: stateValue.value.altitude, antennaHeight: undefined // Destroy antennaHeight if it is 0
             });
        }
        return Object.assign(Object.assign({}, (0, location_field_1.extractLocationRaw)(stateValue)), { deviceAltitude: stateValue.antennaHeight + stateValue.value.altitude, groundAltitude: stateValue.value.altitude, antennaHeight: stateValue.antennaHeight });
    }
    get provider() {
        const stateValue = this.stateValue;
        if (stateValue == null || stateValue.provider == null) {
            return CollectionModelFieldAnchor.PROVIDER_MANUAL;
        }
        return stateValue.provider;
    }
    get antennaHeight() {
        const stateValue = this.stateValue;
        if (stateValue == null || stateValue.antennaHeight == null) {
            return 0;
        }
        return stateValue.antennaHeight;
    }
    target(source) {
        if (CollectionModelFieldAnchor.isCollectionWithVectorTool(this.value)) {
            const bearing = this.value.bearing;
            const distance = this.value.distance;
            return geodesic_1.IkeGeodesic.projectLocation(source, bearing, distance);
        }
        if (CollectionModelFieldAnchor.isCollectionWithLocationTool(this.value)) {
            return {
                altitude: this.value.altitude,
                longitude: this.value.longitude,
                latitude: this.value.latitude
            };
        }
        return null;
    }
    distanceBearingPitch(source) {
        if (!this.value) {
            return {
                distance: 0,
                bearing: 0,
                pitch: 0
            };
        }
        if (CollectionModelFieldAnchor.isCollectionWithVectorTool(this.value)) {
            return {
                distance: this.value.distance,
                bearing: this.value.bearing,
                pitch: this.value.pitch
            };
        }
        if (CollectionModelFieldAnchor.isCollectionWithLocationTool(this.value)) {
            return geodesic_1.IkeGeodesic.getVector(source, this.stateValue.value);
        }
        return null;
    }
    isManual() {
        return this.provider === CollectionModelFieldAnchor.PROVIDER_MANUAL;
    }
    isPointToPoint() {
        return this.provider === CollectionModelFieldAnchor.PROVIDER_P2P;
    }
    isGPS() {
        return this.provider === CollectionModelFieldAnchor.PROVIDER_GPS;
    }
    isTrueSize() {
        return false;
    }
    // Get tool used for collection, by default we show a Vector tool
    get tool() {
        const stateValue = this.stateValue;
        if (stateValue == null || stateValue.value.collectionTool == null) {
            return CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR;
        }
        return stateValue.value.collectionTool;
    }
    getOwnedCaptures() {
        return this.getCaptures();
    }
    getCaptures() {
        const captures = [];
        const captureIds = this.getCaptureIds();
        for (const captureId of captureIds) {
            const capture = this.collection.state.getCapture(captureId);
            if (capture == null) {
                return [];
            }
            if (capture.orientation == null) {
                throw new Error(`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing orientation)`);
            }
            if (capture.calibrationid == null) {
                throw new Error(`Invalid truesize capture '${capture.objectId}' for field '${this.id}' (missing calibrationId)`);
            }
            captures.push(capture);
        }
        return captures;
    }
    isCollectionLink() {
        return false;
    }
    getOwnedCaptureIds() {
        return this.getCaptureIds();
    }
    getCaptureIds() {
        if (!this.isPointToPoint()) {
            return [];
        }
        return this.stateValue.captures;
    }
    hasValue() {
        if (this.value != null) {
            if (this.tool === CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION) {
                return location_field_1.CollectionModelFieldLocation.isValidLocation(this.value);
            }
            if (this.value.distance === 0 && this.value.bearing === 0 && this.value.placement === null) {
                return false;
            }
            return true;
        }
        return false;
    }
}
exports.CollectionModelFieldAnchor = CollectionModelFieldAnchor;
CollectionModelFieldAnchor.PROVIDER_MANUAL = 'manual';
CollectionModelFieldAnchor.PROVIDER_P2P = 'pointtopoint';
CollectionModelFieldAnchor.PROVIDER_GPS = 'gps';
CollectionModelFieldAnchor.PROVIDER_EDITED = 'edited';
CollectionModelFieldAnchor.COLLLECTION_TOOL_VECTOR = 'VECTOR';
CollectionModelFieldAnchor.COLLLECTION_TOOL_LOCATION = 'LOCATION';
CollectionModelFieldAnchor.DEFAULT_PLACEMENT = 'Other';
CollectionModelFieldAnchor.TYPE = core_api_1.IkeFieldType.ANCHOR;
//# sourceMappingURL=anchor.field.js.map